import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { CameraProfile } from '@editor/interfaces/camera'
import { Editor } from '@editor/interfaces/canvas'
import { EditorButton, EditorConfig } from '@editor/interfaces/editor'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { useFocusItem } from '@editor/pages/projects/editor/hooks/useFocusItem'
import { setDisablePanning, setDisableRotation } from '@editor/pages/projects/editor/store/editor'
import { updateInspectionItems } from '@editor/pages/projects/editor/store/inspectionItems'
import { getCameraProfile } from '@editor/services/Camera'
import { patchInspectionArea } from '@editor/services/InspectionArea'
import { patchInspectionItem } from '@editor/services/InspectionSheet'
import { getCentroidOfPolygon3d } from '@editor/services/Points'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { patchInspectionAreas } from '@editor/store/page'
import { useToolUtilSelector } from '@editor/store/selectors'
import { setCursor } from '@editor/store/tool'
import * as Sentry from '@sentry/react'
import { FaForwardStep } from 'react-icons/fa6'
import { Vector3 } from 'three'

import { CameraProfileTarget } from '../definitions'
import {
  SelectionStage,
  reset,
  resetWorkingState,
  setCameraProfile,
  setInitCompleted,
  setSelectionStage,
} from '../store'

const attentionMessages = {
  [SelectionStage.DRAW]: '正面を指定するために、3点を選択してください。',
  [SelectionStage.ROTATE]:
    '面の位置を確認してください。点を移動して調整することもできます。正面を指定ボタンをクリックして次に進んでください。',
  [SelectionStage.FOCUS]:
    'カメラを回転、ズーム、平行移動してさらに調整できます。面に対して横方向に回転することはできません。点群を選択するとその点を画面の中心に設定できます。',
  [SelectionStage.SAVING]: '正面視野を保存中...',
}

export default function useEditor(editorContext: Editor): EditorConfig {
  const { initCompleted, arcballControlsRef, cameraRef } = editorContext

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)
  const focusItem = useFocusItem(editorContext)

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionSheet = useAppSelector((state) => state.inspectionItems.inspectionSheet)

  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const prevSelectedTool = useAppSelector((state) => state.editor.prevSelectedTool)
  const pcdBoundingBoxCenter = useAppSelector((state) => state.editor.pcdBoundingBoxCenter)
  const pcdBoundingSphereRadius = useAppSelector((state) => state.editor.pcdBoundingSphereRadius)
  const initCameraCompleted = useAppSelector((state) => state.editor.initCameraCompleted)
  const initControlsCompleted = useAppSelector((state) => state.editor.initControlsCompleted)

  const focusedElementId = useToolUtilSelector((state) => state.focusedElementId)

  const selectionStage = useAppSelector((state) => state.toolCameraProfile.selectionStage)
  const isDirty = useAppSelector((state) => state.toolCameraProfile.isDirty)
  const drawnPlanePoints = useAppSelector((state) => state.toolCameraProfile.drawnPlanePoints)
  const isEditingItemProfile = useAppSelector(
    (state) =>
      state.toolCameraProfile.profileTarget === CameraProfileTarget.Item &&
      state.editor.selectedTool === EDITOR_TOOLS.CAMERA_PROFILE,
  )

  // Vars
  const isToolSelected = useMemo(() => selectedTool === EDITOR_TOOLS.CAMERA_PROFILE, [selectedTool])
  const isPreviousToolSelected = useMemo(() => prevSelectedTool === EDITOR_TOOLS.CAMERA_PROFILE, [prevSelectedTool])

  // Refs
  const [isFocusing, setIsFocusing] = useState(false)
  const cameraProfile = useRef<CameraProfile>(undefined)
  useEffect(() => {
    if (initCompleted) {
      if (inspectionArea?.camera_profile) {
        cameraProfile.current = inspectionArea.camera_profile
        dispatch(setCameraProfile(inspectionArea.camera_profile))
      } else {
        cameraProfile.current = undefined
        dispatch(setCameraProfile(undefined))
      }
    }
  }, [initCompleted, cameraRef, arcballControlsRef, inspectionArea?.camera_profile, dispatch])

  /**
   * Generate camera profile.
   */
  const generateCameraProfile = useCallback((): CameraProfile | null => {
    if (!cameraRef.current || !arcballControlsRef.current) return null

    const cameraUp = new Vector3().setFromMatrixColumn(cameraRef.current.matrix, 1)
    const targetV3 = arcballControlsRef.current.gizmos.position.clone()
    const direction = cameraRef.current.getWorldDirection(new Vector3()).normalize()
    const left = new Vector3().crossVectors(cameraUp, direction).normalize()
    const up = new Vector3().crossVectors(direction, left).normalize()
    const distance = cameraRef.current.position.distanceTo(targetV3)

    const profile = getCameraProfile(cameraRef.current, arcballControlsRef.current)
    if (!profile) return null

    return {
      ...profile,
      positions: {
        ...profile.positions,
        back: new Vector3().copy(targetV3).sub(direction.clone().multiplyScalar(-distance)).toArray(),
        top: new Vector3().copy(targetV3).add(up.clone().multiplyScalar(distance)).toArray(),
        bottom: new Vector3().copy(targetV3).add(up.clone().negate().multiplyScalar(distance)).toArray(),
        left: new Vector3().copy(targetV3).add(left.clone().multiplyScalar(distance)).toArray(),
        right: new Vector3().copy(targetV3).add(left.clone().negate().multiplyScalar(distance)).toArray(),
      },
    }
  }, [arcballControlsRef, cameraRef])

  /**
   * Load camera profile.
   */
  useEffect(() => {
    if (!initCompleted || !initCameraCompleted || !initControlsCompleted) {
      return
    }

    if (!arcballControlsRef.current || !cameraRef.current) return

    // initialize saved camera profile
    if (cameraProfile.current) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      ;(arcballControlsRef.current as any).setStateFromJSON(JSON.stringify(cameraProfile.current.state))
    } else if (pcdBoundingBoxCenter && pcdBoundingSphereRadius) {
      cameraRef.current.position.set(
        pcdBoundingBoxCenter[0],
        pcdBoundingBoxCenter[1] - pcdBoundingSphereRadius,
        pcdBoundingBoxCenter[2],
      )
      cameraRef.current.up.set(0, 0, 1)
      cameraRef.current.updateMatrixWorld()

      arcballControlsRef.current.target.set(pcdBoundingBoxCenter[0], pcdBoundingBoxCenter[1], pcdBoundingBoxCenter[2])
      arcballControlsRef.current.update()
    }

    dispatch(setInitCompleted(true))
  }, [
    dispatch,
    initCompleted,
    pcdBoundingBoxCenter,
    pcdBoundingSphereRadius,
    initCameraCompleted,
    initControlsCompleted,
    inspectionArea?.inspection_area_id,
    cameraRef,
    arcballControlsRef,
  ])

  /**
   * Save camera profile.
   */
  const onSubmit = useCallback(async () => {
    const profile = generateCameraProfile()
    if (!project?.project_id || !inspectionArea || !profile || isFocusing || !inspectionSheet) return

    const accessToken = await getAccessToken()
    if (!accessToken) {
      return
    }

    dispatch(setSelectionStage(SelectionStage.SAVING))

    // Switch destination depending on what the profile is for.
    let success = false
    if (isEditingItemProfile) {
      const itemResult = await patchInspectionItem(
        accessToken,
        project.project_id,
        inspectionArea.inspection_area_id,
        inspectionSheet.inspection_sheet_id,
        {
          inspection_item_id: focusedElementId,
          camera_profile: {
            target: profile.target,
            state: profile.state,
            positions: {
              front: profile.positions.front,
            },
          },
        },
        showErrorModal,
      )

      if (itemResult) {
        dispatch(updateInspectionItems([itemResult]))
        success = true
      }
    } else {
      const result = await patchInspectionArea(
        accessToken,
        project.project_id,
        {
          ...inspectionArea,
          camera_profile: profile,
        },
        showErrorModal,
      )

      if (result) {
        dispatch(patchInspectionAreas([result]))
        success = true
      }
    }

    // Change to move tool once saved
    if (success) {
      document.dispatchEvent(new CustomEvent('editor:job-completed', { detail: { tool: EDITOR_TOOLS.CAMERA_PROFILE } }))
    } else {
      dispatch(setSelectionStage(SelectionStage.FOCUS))
    }
  }, [
    project?.project_id,
    inspectionArea,
    isEditingItemProfile,
    focusedElementId,
    inspectionSheet,
    isFocusing,
    getAccessToken,
    showErrorModal,
    generateCameraProfile,
    dispatch,
  ])

  /**
   * Set attention text and cursor when tool is active, according to the selection stage.
   */
  useEffect(() => {
    if (isToolSelected) {
      if (attentionMessages[selectionStage]) {
        dispatch(setAttentionText({ message: attentionMessages[selectionStage] }))
      }
    } else if (isPreviousToolSelected) {
      dispatch(setAttentionText({ message: '' }))
      dispatch(setDisablePanning(false))
      dispatch(setDisableRotation(false))
    }
  }, [isPreviousToolSelected, isToolSelected, selectedTool, selectionStage, dispatch])

  /**
   * Reset working state when tool is deselected.
   */
  useEffect(() => {
    if (!isToolSelected && isPreviousToolSelected) {
      dispatch(resetWorkingState())
    }
  }, [isToolSelected, isPreviousToolSelected, dispatch])

  /**
   * Reset on unmount.
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  /**
   * Depending on the selection stage, the submit button will have different behavior.
   */
  const submitButton = useMemo((): EditorButton => {
    const commonProps = {
      isShown: () => isToolSelected,
    }

    if ([SelectionStage.DRAW, SelectionStage.ROTATE].includes(selectionStage)) {
      return {
        ...commonProps,
        key: 'confirm-camera-profile',
        label: '正面を指定',
        loadingLabel: '',
        icon: <FaForwardStep />,
        onClick: () => {
          if (!arcballControlsRef.current || !cameraRef.current) return

          dispatch(setSelectionStage(SelectionStage.FOCUS))
          dispatch(setCursor(CursorState.GRAB))
          setIsFocusing(true)

          const tgt = getCentroidOfPolygon3d(drawnPlanePoints.map((p) => new Vector3(...p)))
          void focusItem(drawnPlanePoints, {
            skipProfile: true,
            distance: cameraRef.current.position.distanceTo(tgt),
          })
            .catch((err) => {
              Sentry.captureException(err, {
                tags: {
                  tool: 'camera-profile',
                  action: 'focusOnTarget',
                },
              })
            })
            .finally(() => {
              dispatch(setDisableRotation(true))
              setIsFocusing(false)
            })
        },
        isLoading: () => false,
        isDisabled: () => selectionStage === SelectionStage.DRAW,
      }
    }

    return {
      ...commonProps,
      key: 'save-camera-profile',
      label: '保存',
      loadingLabel: '保存中',
      onClick: onSubmit,
      isLoading: () => selectionStage === SelectionStage.SAVING || isFocusing,
      isDisabled: () => [SelectionStage.SAVING].includes(selectionStage) || isFocusing,
    }
  }, [
    selectionStage,
    isToolSelected,
    arcballControlsRef,
    cameraRef,
    drawnPlanePoints,
    isFocusing,
    focusItem,
    dispatch,
    onSubmit,
  ])

  return {
    buttons: {
      apply: submitButton,
      reset: {
        onClick: useCallback(() => {
          dispatch(resetWorkingState())
          dispatch(setDisablePanning(false))
          dispatch(setDisableRotation(false))
        }, [dispatch]),

        isShown: useCallback(() => isToolSelected, [isToolSelected]),
        isDisabled: () => !isDirty || selectionStage === SelectionStage.SAVING || isFocusing,
      },
      undo: {
        label: selectionStage === SelectionStage.FOCUS ? '正面の編集に戻る' : undefined,
        isShown: useCallback(
          () => isToolSelected && ![SelectionStage.SAVING].includes(selectionStage),
          [isToolSelected, selectionStage],
        ),
        isLoading: () => isFocusing,
        isDisabled: () => !isDirty || selectionStage === SelectionStage.SAVING || isFocusing,
      },
    },
  }
}
